import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { cn } from "@/utils/cn"

import {
  filterSections,
  findActiveMention,
  parseTokens,
  splitIntoSegments,
  TOKEN_MARKER,
  type ActiveMention,
  type MentionOption,
  type MentionSection,
  type ParsedToken,
} from "./metricPromptMentionTokens"

interface MetricPromptMentionInputProps {
  value: string
  onChange: (value: string) => void
  sections: MentionSection[]
  placeholder?: string
  className?: string
  textAreaRef?: React.Ref<HTMLTextAreaElement>
}

interface FlatMatch {
  sectionIndex: number
  optionIndex: number
}

function flattenMatches(sections: MentionSection[]): FlatMatch[] {
  const flat: FlatMatch[] = []
  sections.forEach((section, sectionIndex) => {
    section.options.forEach((_, optionIndex) => {
      flat.push({ sectionIndex, optionIndex })
    })
  })
  return flat
}

interface CaretCoords {
  top: number
  left: number
  lineHeight: number
}

const CARET_MIRROR_CSS_PROPERTIES = [
  "box-sizing",
  "width",
  "height",
  "overflow-x",
  "overflow-y",
  "border-top-width",
  "border-right-width",
  "border-bottom-width",
  "border-left-width",
  "border-style",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "font-style",
  "font-variant",
  "font-weight",
  "font-stretch",
  "font-size",
  "font-size-adjust",
  "line-height",
  "font-family",
  "text-align",
  "text-transform",
  "text-indent",
  "text-decoration",
  "letter-spacing",
  "word-spacing",
  "tab-size",
] as const

/**
 * Measure where a given character index renders inside a textarea by building
 * a hidden mirror <div> with identical styles + text content up to that index,
 * then reading the position of a marker span. Returns coords relative to the
 * textarea's own box (not the viewport). Callers add `textarea.offsetTop/Left`
 * if they need container-relative coords.
 */
function measureCaretCoords(
  textarea: HTMLTextAreaElement,
  index: number,
): CaretCoords {
  const style = window.getComputedStyle(textarea)
  const mirror = document.createElement("div")
  mirror.setAttribute("aria-hidden", "true")
  mirror.style.position = "absolute"
  mirror.style.visibility = "hidden"
  mirror.style.whiteSpace = "pre-wrap"
  mirror.style.wordWrap = "break-word"
  mirror.style.overflowWrap = "break-word"
  mirror.style.wordBreak = "break-word"
  mirror.style.top = "0"
  mirror.style.left = "-9999px"
  for (const property of CARET_MIRROR_CSS_PROPERTIES) {
    mirror.style.setProperty(property, style.getPropertyValue(property))
  }
  // Textareas do not honor fixed heights for content layout; let it grow.
  mirror.style.height = "auto"

  const before = textarea.value.slice(0, index)
  mirror.textContent = before
  const marker = document.createElement("span")
  marker.textContent = textarea.value.slice(index) || "."
  mirror.appendChild(marker)
  document.body.appendChild(mirror)

  const coords: CaretCoords = {
    top: marker.offsetTop - textarea.scrollTop,
    left: marker.offsetLeft - textarea.scrollLeft,
    lineHeight: parseInt(style.lineHeight, 10) || marker.offsetHeight || 20,
  }
  document.body.removeChild(mirror)
  return coords
}

/**
 * Textarea with a floating, grouped `@`-mention picker. Typing `@` opens the
 * picker; further keystrokes filter options live. Selecting an option replaces
 * the active `@query` span with `@{tokenPrefix}.{key}`.
 *
 * Pure UI — it does not fetch attribute data. The parent supplies `sections`.
 */
export const MetricPromptMentionInput: React.FC<
  MetricPromptMentionInputProps
> = ({ value, onChange, sections, placeholder, className, textAreaRef }) => {
  const internalRef = useRef<HTMLTextAreaElement>(null)
  const setTextareaRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      internalRef.current = node
      if (typeof textAreaRef === "function") {
        textAreaRef(node)
      } else if (textAreaRef) {
        ;(
          textAreaRef as React.MutableRefObject<HTMLTextAreaElement | null>
        ).current = node
      }
    },
    [textAreaRef],
  )

  const overlayRef = useRef<HTMLDivElement>(null)
  const [mention, setMention] = useState<ActiveMention | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [caret, setCaret] = useState<CaretCoords | null>(null)
  const [editingToken, setEditingToken] = useState<ParsedToken | null>(null)

  const isPickerOpen = mention !== null || editingToken !== null

  // When the mention query contains a "." (e.g. `@agent.disp`), scope the
  // picker to only the matching section and filter by the text after the dot.
  // When editing an existing chip, scope to that chip's section with no
  // query filter.
  const filteredSections = useMemo(() => {
    if (editingToken) {
      const scoped = sections.find(
        (s) => s.tokenPrefix === editingToken.section,
      )
      return scoped ? [scoped] : []
    }
    if (!mention) {
      return [] as MentionSection[]
    }
    const dotIndex = mention.query.indexOf(".")
    if (dotIndex === -1) {
      return filterSections(sections, mention.query)
    }
    const prefix = mention.query.slice(0, dotIndex)
    const rest = mention.query.slice(dotIndex + 1)
    const scoped = sections.find((s) => s.tokenPrefix === prefix)
    if (scoped) {
      return filterSections([scoped], rest)
    }
    return filterSections(sections, mention.query)
  }, [mention, editingToken, sections])
  const flatMatches = useMemo(
    () => flattenMatches(filteredSections),
    [filteredSections],
  )

  // Clamp the active index whenever the filtered set shrinks.
  useEffect(() => {
    if (flatMatches.length === 0) {
      setActiveIndex(0)
      return
    }
    setActiveIndex((prev) => Math.min(prev, flatMatches.length - 1))
  }, [flatMatches.length])

  const closePicker = useCallback(() => {
    setMention(null)
    setEditingToken(null)
    setActiveIndex(0)
  }, [])

  const refreshMention = useCallback(
    (text: string, cursor: number) => {
      const next = findActiveMention(text, cursor)
      // Suppress when the cursor lands right after a valid, complete chip —
      // parseTokens validates against `sections`, so a token ending at cursor
      // means the user finished the chip and is likely about to keep typing.
      const atCompleteToken =
        next !== null &&
        text[next.atIndex] === TOKEN_MARKER &&
        parseTokens(text, sections).some((t) => t.end === cursor)
      const effective = atCompleteToken ? null : next

      setMention(effective)
      if (!effective) {
        setActiveIndex(0)
        setCaret(null)
        return
      }
      if (internalRef.current) {
        setCaret(measureCaretCoords(internalRef.current, effective.atIndex))
      }
    },
    [sections],
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const nextValue = event.target.value
      const cursor = event.target.selectionStart
      onChange(nextValue)
      // If the user types while editing a chip, the token's indices would go
      // stale — cancel the chip-edit and fall back to normal mention detection.
      setEditingToken(null)
      refreshMention(nextValue, cursor)
    },
    [onChange, refreshMention],
  )

  const handleSelect = useCallback(
    (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
      const target = event.target as HTMLTextAreaElement
      refreshMention(target.value, target.selectionStart)
    },
    [refreshMention],
  )

  const insertOption = useCallback(
    (section: MentionSection, option: MentionOption) => {
      if (!internalRef.current) {
        return
      }
      let from: number
      let to: number
      if (editingToken) {
        from = editingToken.start
        to = editingToken.end
      } else if (mention) {
        from = mention.atIndex
        to = mention.rangeEnd
      } else {
        return
      }
      const textarea = internalRef.current
      const token = `${TOKEN_MARKER}${section.tokenPrefix}.${option.key}`
      const next = `${value.slice(0, from)}${token}${value.slice(to)}`
      const cursor = from + token.length
      onChange(next)
      closePicker()
      requestAnimationFrame(() => {
        if (internalRef.current === textarea) {
          textarea.focus()
          textarea.setSelectionRange(cursor, cursor)
        }
      })
    },
    [mention, editingToken, value, onChange, closePicker],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!isPickerOpen || flatMatches.length === 0) {
        return
      }
      if (event.key === "ArrowDown") {
        event.preventDefault()
        setActiveIndex((prev) => Math.min(prev + 1, flatMatches.length - 1))
      } else if (event.key === "ArrowUp") {
        event.preventDefault()
        setActiveIndex((prev) => Math.max(prev - 1, 0))
      } else if (event.key === "Enter" || event.key === "Tab") {
        const target = flatMatches[activeIndex]
        if (!target) {
          return
        }
        event.preventDefault()
        const section = filteredSections[target.sectionIndex]
        insertOption(section, section.options[target.optionIndex])
      } else if (event.key === "Escape") {
        event.preventDefault()
        closePicker()
      }
    },
    [
      isPickerOpen,
      flatMatches,
      activeIndex,
      filteredSections,
      insertOption,
      closePicker,
    ],
  )

  const handleBlur = useCallback(() => {
    // Small delay so an onMouseDown on a picker row still fires before close.
    setTimeout(closePicker, 120)
  }, [closePicker])

  const tokens = useMemo(() => parseTokens(value, sections), [value, sections])
  const segments = useMemo(
    () => splitIntoSegments(value, tokens),
    [value, tokens],
  )

  // Clicking a chip leaves the chip in place, opens the picker scoped to its
  // section, and waits for the user to pick another attribute. Selecting one
  // replaces the chip; dismissing (Escape / click-outside) leaves it intact.
  const editToken = useCallback((token: ParsedToken) => {
    setEditingToken(token)
    setMention(null)
    setActiveIndex(0)
    requestAnimationFrame(() => {
      const textarea = internalRef.current
      if (!textarea) {
        return
      }
      textarea.focus()
      textarea.setSelectionRange(token.end, token.end)
      setCaret(measureCaretCoords(textarea, token.start))
    })
  }, [])

  let runningIndex = 0

  return (
    <div className="relative w-full">
      <textarea
        ref={setTextareaRef}
        value={value}
        onChange={handleChange}
        onSelect={handleSelect}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={cn(className, "relative bg-transparent")}
        style={{
          WebkitTextFillColor: "transparent",
          color: "transparent",
          caretColor: "var(--coval-text)",
          // Textareas default to the UA form-control font; the overlay div
          // uses the page font. Without forcing inherit here, glyph widths
          // differ and clicks land on a different character than what's drawn.
          fontFamily: "inherit",
          fontStyle: "inherit",
          fontWeight: "inherit",
          fontVariant: "inherit",
          fontFeatureSettings: "inherit",
          letterSpacing: "inherit",
          wordSpacing: "inherit",
          textIndent: "inherit",
          textTransform: "inherit",
          overflowWrap: "break-word",
          wordBreak: "break-word",
          padding: 0,
          margin: 0,
          // Auto-grow with content. With no internal scroll, the textarea
          // and the overlay can't desync — the surrounding container (which
          // already has max-h + overflow-y-auto) becomes the single source
          // of scroll for both elements.
          fieldSizing: "content",
          resize: "none",
          overflow: "hidden",
        }}
      />
      {/* Chip overlay: sits above the textarea, passes pointer events through
          except on chip buttons. The textarea auto-sizes (field-sizing) so
          both elements have identical layout — no scroll sync needed. */}
      <div
        ref={overlayRef}
        aria-hidden
        className={cn(
          className,
          "absolute inset-0 pointer-events-none whitespace-pre-wrap break-words resize-none",
        )}
        style={{
          letterSpacing: "inherit",
          wordSpacing: "inherit",
          textIndent: "inherit",
          textTransform: "inherit",
          overflowWrap: "break-word",
          wordBreak: "break-word",
          padding: 0,
          margin: 0,
        }}
      >
        {value.length === 0 && placeholder ? (
          <span className="text-coval-text-muted">{placeholder}</span>
        ) : (
          segments.map((segment) =>
            segment.type === "text" ? (
              <span key={segment.key} className="text-coval-text">
                {segment.content}
              </span>
            ) : (
              <button
                key={segment.key}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => editToken(segment.token)}
                className="pointer-events-auto inline rounded bg-surface-2 dark:bg-surface-1 text-coval-text cursor-pointer hover:bg-surface-2 dark:hover:bg-surface-2 transition-colors"
                style={{
                  padding: 0,
                  margin: 0,
                  font: "inherit",
                  fontWeight: "inherit",
                  border: "none",
                  verticalAlign: "baseline",
                  lineHeight: "inherit",
                  letterSpacing: "inherit",
                }}
                aria-label={`Edit attribute ${segment.token.fullMatch}`}
              >
                {`${segment.token.section}.${segment.token.key}`}
              </button>
            ),
          )
        )}
        {/* Trailing zero-width space guarantees the overlay grows with the
            final newline so wrapping and height match the textarea. */}
        <span>&#x200B;</span>
      </div>
      {isPickerOpen &&
      flatMatches.length > 0 &&
      caret &&
      internalRef.current ? (
        <div
          className="absolute z-30 max-h-80 w-80 overflow-auto rounded-md border border-coval-border bg-surface-default shadow-md py-1"
          role="listbox"
          aria-label="Attribute picker"
          tabIndex={-1}
          onMouseDown={(event) => event.preventDefault()}
          style={{
            top:
              internalRef.current.offsetTop + caret.top + caret.lineHeight + 4,
            left: internalRef.current.offsetLeft + caret.left,
          }}
        >
          {filteredSections.map((section) => (
            <div key={section.id} className="mb-1 last:mb-0">
              <div className="px-3 pt-2 pb-1 text-xs font-medium text-coval-text-muted uppercase tracking-wide">
                {section.label}
              </div>
              {section.options.map((option) => {
                const isActive = flatMatches[activeIndex]
                  ? filteredSections[flatMatches[activeIndex].sectionIndex]
                      ?.id === section.id &&
                    filteredSections[flatMatches[activeIndex].sectionIndex]
                      ?.options[flatMatches[activeIndex].optionIndex]?.key ===
                      option.key
                  : false
                const myIndex = runningIndex++
                return (
                  <button
                    key={`${section.id}:${option.key}`}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onMouseEnter={() => setActiveIndex(myIndex)}
                    onClick={() => insertOption(section, option)}
                    className={cn(
                      "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors",
                      isActive
                        ? "bg-surface-1 dark:bg-surface-1"
                        : "hover:bg-surface-1 dark:hover:bg-surface-1",
                      section.accentClassName,
                    )}
                  >
                    <span className="font-mono text-coval-text-muted shrink-0">
                      {`${section.tokenPrefix}.`}
                    </span>
                    <span className="flex-1 truncate text-coval-text">
                      {option.key}
                    </span>
                    {option.description ? (
                      <span className="text-coval-text-muted text-xs truncate max-w-[40%]">
                        {option.description}
                      </span>
                    ) : null}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
